export class PlayerManager {
    constructor() {
        this.players = new Map();
        this._localId = null;
        this.localChangeListeners = new Set();
    }
    /** Back-compat: read-only alias so existing code `playerManager.localPlayer` keeps working */
    get localPlayer() { return this.local; }
    /** Canonical getter for the user/player on this client */
    get local() {
        var _a;
        return this._localId ? ((_a = this.players.get(this._localId)) !== null && _a !== void 0 ? _a : null) : null;
    }
    /** Add a player. If `opts.isLocal` is true, mark as local (optionally replacing an existing one). */
    add(player, opts = {}) {
        if (this.players.has(player.id)) {
            const existing = this.players.get(player.id);
            if (opts.isLocal)
                this.setLocalById(player.id, { replace: opts.replaceLocal });
            return existing;
        }
        this.players.set(player.id, player);
        if (opts.isLocal)
            this.setLocalById(player.id, { replace: opts.replaceLocal });
        return player;
    }
    /** Mark an existing player as local (by id). */
    setLocalById(id, opts = {}) {
        if (!this.players.has(id))
            throw new Error(`setLocalById: no player '${id}'`);
        if (this._localId && this._localId !== id && !opts.replace) {
            throw new Error(`Local player already set to '${this._localId}'. Pass { replace: true } to override.`);
        }
        const prev = this.local;
        this._localId = id;
        this.emitLocalChange(prev, this.local);
    }
    /** Add-and-mark as local in one call. */
    setLocal(player, opts = {}) {
        this.add(player, { isLocal: true, replaceLocal: opts.replace });
    }
    /** Ensure there is a local player; create via factory if missing. */
    ensureLocal(factory) {
        if (this.local)
            return this.local;
        const p = factory();
        this.add(p, { isLocal: true });
        return p;
    }
    /** Get a player by id. */
    get(id) { return this.players.get(id); }
    /** Does a player exist? */
    has(id) { return this.players.has(id); }
    /** All remote (non-local) players as an array. */
    remotes() {
        const out = [];
        for (const [id, p] of this.players)
            if (id !== this._localId)
                out.push(p);
        return out;
    }
    /** Iterate only over remotes. */
    forEachRemote(fn) {
        for (const [id, p] of this.players)
            if (id !== this._localId)
                fn(p);
    }
    /** Remove a player; if it was local, clears the local binding and emits change. */
    remove(id) {
        var _a;
        const wasLocal = this._localId === id;
        const prevLocal = wasLocal ? ((_a = this.players.get(id)) !== null && _a !== void 0 ? _a : null) : null;
        const ok = this.players.delete(id);
        if (wasLocal) {
            this._localId = null;
            this.emitLocalChange(prevLocal, null);
        }
        return ok;
    }
    /** Remove everyone and clear local binding. */
    clear() {
        const prev = this.local;
        this.players.clear();
        this._localId = null;
        this.emitLocalChange(prev, null);
    }
    /** Per-frame update. Default `all`; pass 'local' or 'remotes' to scope. */
    update(dt, scope = 'all') {
        var _a;
        if (scope === 'local') {
            (_a = this.local) === null || _a === void 0 ? void 0 : _a.update(dt);
            return;
        }
        if (scope === 'remotes') {
            this.forEachRemote(p => p.update(dt));
            return;
        }
        for (const p of this.players.values())
            p.update(dt);
    }
    /** Subscribe to local player changes. Returns an unsubscribe function. */
    onLocalChange(cb) {
        this.localChangeListeners.add(cb);
        return () => this.localChangeListeners.delete(cb);
    }
    emitLocalChange(prev, next) {
        this.localChangeListeners.forEach(cb => cb(prev, next));
    }
}
export const playerManager = new PlayerManager();
